import React, { useState } from 'react';
import { connect } from 'react-redux';
import { flow, isNumber } from 'lodash';
import { useLocale } from 'locale';
import { Table, TableColumn } from 'ds/components/table';
import {
  ComparableItem,
  HistoryHebrewItem,
  HistoryItem,
  HistoryItemAgent,
  PricesTabType,
} from 'store/state/selectors/insights/prices';
import { HistoryHebrewDateColumn } from './columns/HistoryHebrewDateColumn';
import { getHistoryDateColumnConfig } from './columns/HistoryDateColumn';
import { getHistoryPriceColumnConfig, HistoryPriceColumn } from './columns/HistoryPriceColumn';
import { getHistoryAddressColumnConfig, HistoryAddressColumn } from './columns/HistoryAddressColumn';
import { getHistoryPricePerSqFtColumnConfig, HistoryPricePerSqFtColumn } from './columns/HistoryPricePerSqFtColumn';
import HistoryAgentColumn, { getHistoryAgentColumnConfig } from './columns/agent-column';
import { SmallText } from 'ds/components/typography';
import { InsightType, ResolutionPreference } from 'utils/entities';
import { EMPTY_COL_CHAR, filterItemData, genericColConfig, HistoryTableProps, PricesTableColumn } from '../../utils';
import { setPricesHistoryIndex, setPricesLastExpandedRow as setPricesLastExpandedRowAction } from 'store/state/insightsContext/actions';
import { setActivePoiIds as setActivePoiIdsAction } from 'store/state/searchContext/actions';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { ExpandedInfo } from './ExpandedInfo';
import { activeHistoryIdxSelector } from 'components/map/Insights/customComponents/Prices/selectors';
import { State } from 'store/state';
import { pricesTabSelector } from 'store/state/insightsContext/selectors';
import { TableWrapper, ThisUnitLabel } from './styled';
import { Route, useRoute } from 'config/routes';
import { currentPoiSelector } from 'store/state/selectors/poi';
import { routeSelector } from 'store/state/selectors/router';
import {
  isConnectedUserSelector,
  safeDealsAgentsByIdsSelector,
  safeDealsOfficesByIdsSelector,
  isAgentConsoleUserSelector,
} from 'store/state/domainData/selectors';
import { AuthenticationModalPayload, AuthenticationModalType } from 'components/authentication/types';
import { AuthModalSource } from 'store/state/app';
import { setAuthModal } from 'store/state/app/actions';

const addressNofollowRoutes = [ Route.StreetPage,  Route.LocalPage ];

const getComparableSqFtColumnConfig = (): Partial<TableColumn<ComparableItem>> => ({
  sortFn: (items: ComparableItem[]) => items.sort((a, b) => b.sqFt - a.sqFt),
  width: '10%',
  gap: 16,
  align: 'right',
});

const HistoryTable: React.FC<HistoryTableProps> = ({
  children,
  visibleRowsDesktop,
  setActiveRecordIndex,
  visibleRowsMobile,
  currentBulletinId,
  paginationType = 'pages',
  activeTab,
  isMobile,
  isAgentConsoleUser,
  tab,
  setActivePoiIds,
  setPricesLastExpandedRow,
  activeRecordIndex,
  projectId,
  projectIds,
  isAddressMasked,
  isProjectPage,
  dealsAgentsData,
  dealsOfficesData,
  isConnectedUser,
  onSetAuthModal,
}) => {
  const [ hoveredRowIdx, setHoveredRowIdx ] = useState(null);
  const { t } = useLocale();
  const screen = useScreenBreakpoint();
  const { route } = useRoute();
  const isCurrent = (item: HistoryItem | HistoryHebrewItem) => item.id === currentBulletinId;
  const isCompare = tab.tabId === PricesTabType.Compare;
  const isAddressMaskedNoDistance = (item: HistoryItem | HistoryHebrewItem) => isAddressMasked && !item.distance;
  const addressNofollow = addressNofollowRoutes.includes(route.name as Route) ? 'nofollow' : undefined;

  let columns: Array<TableColumn<HistoryItem>> = [];

  const dateCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Date }),
    renderFn: (item: HistoryItem | HistoryHebrewItem) => (
      <HistoryHebrewDateColumn
        item={item as HistoryHebrewItem}
        isMobile={isMobile}
        tabId={tab.tabId}
        isCurrent={isCurrent(item)}
        isProjectPage={isProjectPage}
        projectId={projectId}
        isAddressMasked={isAddressMasked}
        nofollow={addressNofollow}
      />
    ),
    ...getHistoryDateColumnConfig(isMobile),
  } as TableColumn<HistoryItem>;

  const priceCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Price }),
    renderFn: (item: HistoryItem) => <HistoryPriceColumn item={item} isMobile={isMobile} tabId={tab.tabId} />,
    ...getHistoryPriceColumnConfig(isMobile, tab.tabId),
  } as TableColumn<HistoryItem>;

  const addressCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Address }),
    renderFn: (item: HistoryItem) => (
      <HistoryAddressColumn
        isAddressMaskedNoDistance={isAddressMaskedNoDistance(item)}
        projectIds={projectIds}
        item={item}
        projectId={projectId}
        isCompare={isCompare}
        isCurrent={isCurrent(item)}
        nofollow={addressNofollow}
      />
    ),
    ...getHistoryAddressColumnConfig(),
  } as TableColumn<HistoryItem>;

  const pricePerSqFtCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.PricePerSqFeet }),
    renderFn: (item: HistoryItem) => <HistoryPricePerSqFtColumn item={item} />,
    ...getHistoryPricePerSqFtColumnConfig(tab.tabId),
  } as TableColumn<HistoryItem>;

  const agentCol = {
    ...getHistoryAgentColumnConfig(),
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Agent }),
    renderFn: (item: HistoryItem | HistoryHebrewItem) => <HistoryAgentColumn item={item as HistoryHebrewItem} />,
    customClassName: 'agent-history',
    sortFn: null,
  } as TableColumn<HistoryItem>;

  const bedsCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Beds }),
    renderFn: (item: HistoryItem) => <SmallText>{item.beds || EMPTY_COL_CHAR}</SmallText>,
    sortFn: (items: HistoryItem[]) => items.sort((a, b) => b.beds - a.beds),
    ...genericColConfig,
  } as TableColumn<HistoryItem>;

  const sqFtCol = {
    ...getComparableSqFtColumnConfig(),
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.SqFt }),
    renderFn: (item: HistoryItem) => <SmallText>{item.size || EMPTY_COL_CHAR}</SmallText>,
    sortFn: (items: HistoryItem[]) => items.sort((a, b) => b.size - a.size),
  } as TableColumn<HistoryItem>;

  const floorCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Floor }),
    renderFn: (item: HistoryItem) => (
      <SmallText>{t('widget.prices.floorLabel', {
        floor: item.floor,
        total: isNumber(item.totalFloors) ? item.totalFloors : null,
      }) || EMPTY_COL_CHAR}</SmallText>
    ),
    sortFn: (items: HistoryItem[]) => items.sort((a, b) => b.floor - a.floor),
    ...genericColConfig,
  } as TableColumn<HistoryItem>;

  const yearCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Year }),
    renderFn: (item: HistoryItem) => <SmallText>{item.year || EMPTY_COL_CHAR}</SmallText>,
    sortFn: (items: HistoryItem[]) => items.sort((a, b) => b.year - a.year),
    ...genericColConfig,
  } as TableColumn<HistoryItem>;
  const distanceCol = {
    label: t('widget.prices.tableColumnTitle', { title: PricesTableColumn.Distance }),
    renderFn: (item: HistoryItem) => (
      <SmallText>
        {isCurrent(item)
          ? isAddressMaskedNoDistance(item) ? EMPTY_COL_CHAR : (<ThisUnitLabel>{item.currentPoiLabel}</ThisUnitLabel>)
          : isNumber(item.distance)
            ? t('widget.prices.distance', {
              distance: isAddressMaskedNoDistance(item) ? 'masked' : item.distance,
              isMobile: false,
            })
            : EMPTY_COL_CHAR
        }
      </SmallText>),
    sortFn: (items: HistoryItem[]) => items.sort((a, b) => b.distance - a.distance),
    ...genericColConfig,
    width: '80px',
  } as TableColumn<HistoryItem>;

  const additionalCol = screen === 1 || screen === 3 ? [ null ] : [ floorCol, yearCol ];

  let sortColumn: number = 0;
  if (isMobile) {
    columns = [ dateCol, priceCol ];
  }
  else {
    const includeDistance = route.name !== Route.StreetPage && route.name !== Route.LocalPage;
    columns = [ addressCol, includeDistance ? distanceCol : null, dateCol, priceCol, sqFtCol, pricePerSqFtCol, bedsCol, ...additionalCol, agentCol ].filter(Boolean);
    sortColumn = includeDistance ? 2 : 1;
  }

  // in Building tab (Madlan) do not set client side sorting, so that "arrow" sorting indicator is not shown,
  // by default the back-end groups the records by address and sorts by date
  if (tab.tabId === PricesTabType.Building) {
    sortColumn = null;
  }

  const showExpandOnRowFn = (item: HistoryItem | HistoryHebrewItem) => {
    if (item.id === currentBulletinId) return false;

    let buyer = item.buyer;
    let seller = item.seller;

    if (typeof buyer === 'object' || typeof seller === 'object') {
      buyer = filterItemData(buyer as HistoryItemAgent, dealsAgentsData, dealsOfficesData);
      seller = filterItemData(seller as HistoryItemAgent, dealsAgentsData, dealsOfficesData);
    }

    if (seller || buyer) return true;
    if (currentBulletinId !== item.id && isAgentConsoleUser && !buyer && !seller) return true;
    return (screen === 3) && Boolean(item.year || isNumber(item.floor));
  };

  const data = paginationType ? tab.tableData : tab.tableData.slice(0, isMobile ? visibleRowsMobile : visibleRowsDesktop);

  const handleRowMouseEnter = (record: HistoryItem | HistoryHebrewItem, id: number) => {
    const isBulletinRecord = record.type === 'bulletin';
    if (activeTab === PricesTabType.Compare) {
      if (isBulletinRecord && !isCurrent(record)) setActivePoiIds([ record.id ]);
    }
    else {
      setActiveRecordIndex([ record.id ]);
    }

    setHoveredRowIdx(id);
  };

  const handleRowMouseLeave = () => {
    if (activeTab === PricesTabType.Compare) {
      setActivePoiIds([]);
    }
    else {
      setActiveRecordIndex([]);
    }
    setHoveredRowIdx(null);
  };

  const handleGatedFeatureClick = (featureType: 'sort' | 'pagination', bypassCb: () => void) => {
    if (isConnectedUser) return bypassCb();

    switch (featureType) {
      case 'sort':
        if ([ Route.AddressPage, Route.LocalPage, Route.StreetPage ].includes(route.name)) {
          onSetAuthModal({ type: AuthenticationModalType.GatedDeals }, { source: AuthModalSource.DealsGateTab });
        }
        else bypassCb();
        break;
      case 'pagination':
        onSetAuthModal({ type: AuthenticationModalType.GatedDeals }, { source: AuthModalSource.DealsGatePagination });
        break;
    }
  };

  return (
    <TableWrapper isCompare={isCompare}>
      <ShowAt to={2}>
        {(isMapHiddenRange) => (
          <Table<HistoryItem | HistoryHebrewItem>
            visibleRows={isMobile ? visibleRowsMobile : visibleRowsDesktop}
            insightType={InsightType.Prices}
            initialSortColumn={sortColumn}
            data={data}
            onRowMouseEnter={isMapHiddenRange ? undefined : handleRowMouseEnter}
            onRowMouseLeave={handleRowMouseLeave}
            onRowExpand={(dataItem) => {
              setPricesLastExpandedRow(dataItem.id);
            }}
            activeRowFilter={(record) => hoveredRowIdx !== null ? false : activeRecordIndex.includes(record.id)}
            isExpandable
            columns={columns}
            paginationType={paginationType}
            alwaysShowExpandIcon
            expandCol={ExpandedInfo}
            fixedRowFilter={(item: HistoryItem | HistoryHebrewItem) => isCurrent(item)}
            showExpandOnRowFn={showExpandOnRowFn}
            gatedFeatures={[ 'sort', 'pagination' ]}
            onGatedFeatureClick={handleGatedFeatureClick}
          />
        )}
      </ShowAt>
      {children}
    </TableWrapper>
  );
};

const currentPoiIdSelector = flow(currentPoiSelector, poi => poi ? poi.id : null);
const isAddressMaskedSelector = flow(currentPoiSelector, poi => {
  if (!poi) return false;
  const { resolutionPreferences } = poi.addressDetails || {};
  return resolutionPreferences === ResolutionPreference.Street ||
    resolutionPreferences === ResolutionPreference.Neighborhood;
});

const isProjectPageSelector = flow(routeSelector, (r => r.name === Route.ProjectPage));

const mapStateToProps = (state: State) => ({
  activeRecordIndex: activeHistoryIdxSelector(state),
  currentBulletinId: currentPoiIdSelector(state),
  isAddressMasked: isAddressMaskedSelector(state),
  activeTab: pricesTabSelector(state),
  isProjectPage: isProjectPageSelector(state),
  dealsAgentsData: safeDealsAgentsByIdsSelector(state),
  dealsOfficesData: safeDealsOfficesByIdsSelector(state),
  isConnectedUser: isConnectedUserSelector(state),
  isAgentConsoleUser: isAgentConsoleUserSelector(state),
});

const mapStateToDispatch = {
  setActiveRecordIndex: setPricesHistoryIndex,
  setActivePoiIds: (ids: string[]) => setActivePoiIdsAction(ids, { source: 'map' }),
  setPricesLastExpandedRow: setPricesLastExpandedRowAction,
  onSetAuthModal: (payload: AuthenticationModalPayload, meta: { isUserInitiated?: boolean, source?: AuthModalSource }) => setAuthModal(payload, meta),
};

export default connect(mapStateToProps, mapStateToDispatch)(HistoryTable);
